import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  isValid,
  parse,
  parseISO,
} from "date-fns";
import type { ParsedUrlQuery } from "node:querystring";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

import { routes } from "@zapier/reporting/src/constants";
import {
  FilterChangeValue,
  HistoryFiltersTypes,
  Range,
} from "@zapier/reporting/src/types";
import {
  getFiltersFromParams,
  getQueryFromDateRange,
  getUrlFromParams,
} from "@zapier/reporting/src/utils";
import { getEarliestSelectableDate } from "./getEarliestSelectableDate";
import { resolveSingleDateParam } from "./resolveSingleDateParam";

type UseHistoryRouteFiltersInput = {
  defaultDateRange?: Partial<Range<string>>;
  maxDateRangeDays?: number;
  searchTargetRoute?: string;
};

type ClampResult = {
  filters: HistoryFiltersTypes;
  hasClampedDateRange: boolean;
};

type SingleParamResult = ClampResult & { shouldResetDateParams: boolean };

// Mirrors getDateRangeFromQuery: a param it cannot parse falls back to the
// default range, so it must not be treated as a date the user gave.
function isParsableDateParam(rawDate: string | undefined) {
  if (rawDate === undefined) {
    return false;
  }

  const dateString = rawDate.includes("%")
    ? decodeURIComponent(rawDate)
    : rawDate;

  return dateString.includes("T")
    ? isValid(parseISO(dateString))
    : isValid(parse(dateString, "yyyy-MM-dd", new Date()));
}

function resolveSingleDateParamFilters({
  asPath,
  currentEndDate,
  currentStartDate,
  defaultDateRange,
  parsedFilters,
  query,
}: {
  asPath: string;
  currentEndDate: string | undefined;
  currentStartDate: string | undefined;
  defaultDateRange: Partial<Range<string>> | undefined;
  parsedFilters: HistoryFiltersTypes;
  query: ParsedUrlQuery;
}): SingleParamResult {
  const unchanged = {
    filters: parsedFilters,
    hasClampedDateRange: false,
    shouldResetDateParams: false,
  };
  const fromDate = parseISO(parsedFilters.dateFilter.from);
  const toDate = parseISO(parsedFilters.dateFilter.to);
  if (!isValid(fromDate) || !isValid(toDate)) {
    return unchanged;
  }

  const resolution = resolveSingleDateParam({
    earliest: getEarliestSelectableDate(),
    from: fromDate,
    hasEnd: isParsableDateParam(currentEndDate),
    hasStart: isParsableDateParam(currentStartDate),
    to: toDate,
  });
  if (resolution.kind === "unchanged") {
    return unchanged;
  }

  if (resolution.kind === "range") {
    return {
      filters: {
        ...parsedFilters,
        dateFilter: {
          ...parsedFilters.dateFilter,
          from: resolution.from.toISOString(),
          to: resolution.to.toISOString(),
        },
      },
      hasClampedDateRange: true,
      shouldResetDateParams: false,
    };
  }

  const {
    end_datetime: _end,
    start_datetime: _start,
    ...queryWithoutDates
  } = query;
  const pathWithoutDates = new URL(asPath, "https://zapier.test");
  pathWithoutDates.searchParams.delete("start_datetime");
  pathWithoutDates.searchParams.delete("end_datetime");
  const defaultFilters = getFiltersFromParams(
    queryWithoutDates,
    `${pathWithoutDates.pathname}${pathWithoutDates.search}`,
    defaultDateRange
  );

  return {
    filters: {
      ...parsedFilters,
      dateFilter: defaultFilters.dateFilter,
    },
    hasClampedDateRange: true,
    shouldResetDateParams: true,
  };
}

function clampToLookback(parsedFilters: HistoryFiltersTypes): ClampResult {
  const unchanged = { filters: parsedFilters, hasClampedDateRange: false };
  const fromDate = parseISO(parsedFilters.dateFilter.from);
  const toDate = parseISO(parsedFilters.dateFilter.to);
  if (!isValid(fromDate) || !isValid(toDate)) {
    return unchanged;
  }

  const earliest = getEarliestSelectableDate();
  if (fromDate >= earliest) {
    return unchanged;
  }

  return {
    filters: {
      ...parsedFilters,
      dateFilter: {
        ...parsedFilters.dateFilter,
        from: earliest.toISOString(),
        ...(toDate < earliest ? { to: endOfDay(earliest).toISOString() } : {}),
      },
    },
    hasClampedDateRange: true,
  };
}

function clampToMaxRange(
  parsedFilters: HistoryFiltersTypes,
  maxDateRangeDays?: number
): ClampResult {
  const unchanged = { filters: parsedFilters, hasClampedDateRange: false };
  if (!maxDateRangeDays || maxDateRangeDays < 1) {
    return unchanged;
  }

  const maxDayDiff = maxDateRangeDays - 1;
  const fromDate = parseISO(parsedFilters.dateFilter.from);
  const toDate = parseISO(parsedFilters.dateFilter.to);
  if (!isValid(fromDate) || !isValid(toDate)) {
    return unchanged;
  }

  if (differenceInCalendarDays(toDate, fromDate) <= maxDayDiff) {
    return unchanged;
  }

  return {
    filters: {
      ...parsedFilters,
      dateFilter: {
        ...parsedFilters.dateFilter,
        to: endOfDay(addDays(fromDate, maxDayDiff)).toISOString(),
      },
    },
    hasClampedDateRange: true,
  };
}

export function useHistoryRouteFilters({
  defaultDateRange,
  maxDateRangeDays,
  searchTargetRoute = routes.log,
}: UseHistoryRouteFiltersInput = {}) {
  const router = useRouter();

  const parsedFilters = useMemo(
    () => getFiltersFromParams(router.query, router.asPath, defaultDateRange),
    [defaultDateRange, router.asPath, router.query]
  );

  const {
    currentEndDate,
    currentStartDate,
    hasDateQueryParams,
  }: {
    currentEndDate: string | undefined;
    currentStartDate: string | undefined;
    hasDateQueryParams: boolean;
  } = useMemo(() => {
    const currentUrl = new URL(router.asPath, "https://zapier.test");
    const currentStartDate =
      currentUrl.searchParams.get("start_datetime") || undefined;
    const currentEndDate =
      currentUrl.searchParams.get("end_datetime") || undefined;

    return {
      currentEndDate,
      currentStartDate,
      hasDateQueryParams:
        currentStartDate !== undefined || currentEndDate !== undefined,
    };
  }, [router.asPath]);

  const {
    filters,
    hasClampedDateRange,
    shouldResetDateParams,
  }: {
    filters: HistoryFiltersTypes;
    hasClampedDateRange: boolean;
    shouldResetDateParams: boolean;
  } = useMemo(() => {
    const singleParamResult = resolveSingleDateParamFilters({
      currentEndDate,
      currentStartDate,
      defaultDateRange,
      parsedFilters,
      query: router.query,
      asPath: router.asPath,
    });
    const lookbackResult = clampToLookback(singleParamResult.filters);
    const rangeResult = clampToMaxRange(
      lookbackResult.filters,
      maxDateRangeDays
    );

    return {
      filters: rangeResult.filters,
      hasClampedDateRange:
        singleParamResult.hasClampedDateRange ||
        lookbackResult.hasClampedDateRange ||
        rangeResult.hasClampedDateRange,
      shouldResetDateParams: singleParamResult.shouldResetDateParams,
    };
  }, [
    currentEndDate,
    currentStartDate,
    defaultDateRange,
    maxDateRangeDays,
    parsedFilters,
    router.asPath,
    router.query,
  ]);

  const { asPath, replace } = router;

  useEffect(() => {
    if (!hasClampedDateRange || !hasDateQueryParams) {
      return;
    }

    if (shouldResetDateParams) {
      const resetUrl = new URL(asPath, "https://zapier.test");
      resetUrl.searchParams.delete("start_datetime");
      resetUrl.searchParams.delete("end_datetime");
      replace(
        `${resetUrl.pathname}${resetUrl.search}${resetUrl.hash}`,
        undefined,
        {
          shallow: true,
        }
      );
      return;
    }

    const fromDate = parseISO(filters.dateFilter.from);
    const toDate = parseISO(filters.dateFilter.to);
    if (!isValid(fromDate) || !isValid(toDate)) {
      return;
    }

    const nextDateQueryParams = getQueryFromDateRange(
      { from: fromDate, to: toDate },
      {
        ...router.query,
        ...(currentStartDate ? { start_datetime: currentStartDate } : {}),
        ...(currentEndDate ? { end_datetime: currentEndDate } : {}),
      }
    );
    const nextStartDate = nextDateQueryParams.start_datetime;
    const nextEndDate = nextDateQueryParams.end_datetime;

    if (currentStartDate === nextStartDate && currentEndDate === nextEndDate) {
      return;
    }

    const normalizedUrl = new URL(asPath, "https://zapier.test");
    if (nextStartDate) {
      normalizedUrl.searchParams.set("start_datetime", nextStartDate);
    } else {
      normalizedUrl.searchParams.delete("start_datetime");
    }
    if (nextEndDate) {
      normalizedUrl.searchParams.set("end_datetime", nextEndDate);
    } else {
      normalizedUrl.searchParams.delete("end_datetime");
    }

    const nextAsPath = `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;

    if (nextAsPath !== asPath) {
      replace(nextAsPath, undefined, { shallow: true });
    }
  }, [
    asPath,
    currentEndDate,
    currentStartDate,
    filters.dateFilter.from,
    filters.dateFilter.to,
    hasClampedDateRange,
    hasDateQueryParams,
    replace,
    router.query,
    shouldResetDateParams,
  ]);

  const handleFilterChange = useCallback(
    (filter: keyof HistoryFiltersTypes, value: FilterChangeValue) => {
      if (filter === "search") {
        const searchValue = typeof value === "string" ? value : "";

        // Do not search unless value actually changed
        // do not let "" vs undefined trigger
        if ((searchValue || "") === (filters.search || "")) {
          return;
        }

        router.push(
          getUrlFromParams(
            searchTargetRoute,
            {
              ...filters,
              page: 0,
              search: searchValue,
            },
            router.query
          )
        );
        return;
      }

      const filterChanges: HistoryFiltersTypes = {
        ...filters,
        [filter]: value,
      };

      // Always reset page when another filter changes
      if (filter !== "page" && filters.page !== 1) {
        filterChanges.page = 1;
      }

      if (Object.keys(filterChanges).length > 0) {
        router.push(
          getUrlFromParams(router.pathname, filterChanges, router.query)
        );
      }
    },
    [filters, router, searchTargetRoute]
  );

  const handleFilterReset = useCallback(() => {
    router.push(getUrlFromParams(router.pathname, {}, router.query));
  }, [router]);

  return {
    filters,
    handleFilterChange,
    handleFilterReset,
  };
}
